import { BrowserRouter, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import { AuthProvider } from 'contexts/AuthProvider';
import { SessionProvider } from 'contexts/SessionProvider';
import { LoggingProvider, useLogging } from 'contexts/logging';
import Toast from './components/Toast';

import Router from './routes';
import ThemeProvider from './themes';

function CSPViolationHandler() {
    const { error } = useLogging();

    useEffect(() => {
        const handleCSPViolation = (event) => {
            const violationError = new Error(`CSP Violation: ${event.violatedDirective}`);
            violationError.code = 'CSP_VIOLATION';
            violationError.details = {
                blockedURI: event.blockedURI,
                violatedDirective: event.violatedDirective,
                originalPolicy: event.originalPolicy,
                sourceFile: event.sourceFile,
                lineNumber: event.lineNumber,
                columnNumber: event.columnNumber,
            };
            error(violationError);
        };

        document.addEventListener('securitypolicyviolation', handleCSPViolation);

        return () => {
            document.removeEventListener('securitypolicyviolation', handleCSPViolation);
        };
    }, [error]);

    return null;
}

function App() {
    return (
        <LoggingProvider>
            <CSPViolationHandler />
            <SessionProvider>
                <AuthProvider>
                    <HelmetProvider>
                        <BrowserRouter>
                            <ThemeProvider>
                                <Toast />
                                <PageviewTracker />
                                <Router />
                            </ThemeProvider>
                        </BrowserRouter>
                    </HelmetProvider>
                </AuthProvider>
            </SessionProvider>
        </LoggingProvider>
    );
}

function PageviewTracker() {
    const location = useLocation();

    useEffect(() => {
        try {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                event: 'pageview',
                page: location.pathname + location.search,
            });
        } catch (e) {
            // noop
        }
    }, [location]);

    return null;
}

export default Sentry.withProfiler(App);
